import { useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "@/lib/api";
import { Field, inp } from "@/components/admin/AdminUi";
import { cassa } from "./cassaApi";

const FIELDS = [["code", "Codice prodotto *"], ["description", "Descrizione"], ["compat", "Compatibilità stampanti"], ["cost", "Prezzo di acquisto"], ["price", "Prezzo di vendita"], ["category", "Categoria"], ["points", "Punti premio (facoltativo)"]];

export function ImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [pre, setPre] = useState(null);
  const [map, setMap] = useState({});
  const [name, setName] = useState("");
  const [supplier, setSupplier] = useState("");
  const [prov, setProv] = useState(false);
  const [upd, setUpd] = useState(true);
  const [busy, setBusy] = useState(false);
  const reset = () => { setFile(null); setPre(null); setMap({}); setName(""); setProv(false); };
  const pick = async (f) => {
    if (!f) return;
    setFile(f); setName(f.name.replace(/\.[^.]+$/, "")); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await cassa.post("/import/preview", fd);
      setPre(data); setMap(data.guess);
    } catch (e) { toast.error(errMsg(e)); setFile(null); }
    setBusy(false);
  };
  const run = async () => {
    if (map.code === undefined) return toast.error("Scegli la colonna del codice");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("mapping", JSON.stringify(map)); fd.append("name", name); fd.append("provisional", prov); fd.append("update_existing", upd); fd.append("supplier", supplier);
      const { data } = await cassa.post("/import", fd);
      toast.success(`Importati ${data.inserted} articoli${data.updated ? `, aggiornati ${data.updated}` : ""}${data.skipped ? `, ${data.skipped} righe senza codice saltate` : ""}`);
      reset(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cassa-import-dialog">
        <DialogHeader><DialogTitle className="text-2xl font-extrabold uppercase">Carica listino</DialogTitle></DialogHeader>
        {!pre ? (
          <label className="block rounded-xl border-2 border-dashed border-slate-300 p-10 text-center cursor-pointer hover:border-brand-navy" data-testid="cassa-import-drop">
            {busy ? <Loader2 className="w-8 h-8 mx-auto animate-spin" /> : <FileUp className="w-8 h-8 mx-auto text-brand-navy" />}
            <div className="mt-2 font-semibold">Scegli il file Excel (.xlsx) o CSV</div>
            <div className="text-xs text-slate-500">Colonne: codice, descrizione, compatibilità, prezzo acquisto, prezzo vendita</div>
            <input data-testid="cassa-import-file" type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden" onChange={(e) => pick(e.target.files[0])} />
          </label>
        ) : (
          <div className="space-y-4">
            <p className="text-sm"><b>{file?.name}</b> · {pre.total} righe. Controlla l'abbinamento delle colonne:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, l]) => (
                <Field key={k} label={l}>
                  <select data-testid={`cassa-map-${k}`} className={inp} value={map[k] ?? ""} onChange={(e) => setMap({ ...map, [k]: e.target.value === "" ? undefined : +e.target.value })}>
                    <option value="">— non presente —</option>{pre.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </Field>
              ))}
            </div>
            <div className="overflow-x-auto rounded border">
              <table className="text-xs w-full"><thead className="bg-slate-50"><tr>{FIELDS.map(([k, l]) => <th key={k} className="px-2 py-1 text-left">{l.replace(" *", "")}</th>)}</tr></thead>
                <tbody>{pre.rows.slice(0, 5).map((r, i) => <tr key={i} className="border-t">{FIELDS.map(([k]) => <td key={k} className="px-2 py-1 max-w-[220px] truncate">{map[k] !== undefined ? r[map[k]] : ""}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <Field label="Nome del listino"><input data-testid="cassa-import-name" className={inp} value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Fornitore (es. Fornitore A): i listini dello stesso fornitore si aggiornano tra loro, quelli di fornitori diversi si confrontano in colonne">
              <input data-testid="cassa-import-supplier" className={inp} value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="vuoto = nome del listino" />
            </Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="cassa-import-provisional" className="w-4 h-4 accent-brand-navy" checked={prov} onChange={(e) => setProv(e.target.checked)} /> Listino di prova (provvisorio): lo elimini poi con un clic</label>
            {!prov && <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="cassa-import-update" className="w-4 h-4 accent-brand-navy" checked={upd} onChange={(e) => setUpd(e.target.checked)} /> Aggiorna gli articoli già presenti con lo stesso codice</label>}
            <button data-testid="cassa-import-run" onClick={run} disabled={busy} className="press w-full h-11 rounded-md bg-brand-yellow text-brand-navy font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />} Importa {pre.total} righe
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
